import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import { FiEdit2, FiTrash2, FiCheck, FiX, FiPlus, FiLoader } from 'react-icons/fi';
import { TAG_COLORS } from '../../components/Constantes/Colors';

function CategoryManager({ categories, setCategories, onClose }) {
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editColor, setEditColor] = useState(1); // index 1-11
    const [inputValue, setInputValue] = useState('');
    const [inputColor, setInputColor] = useState(1); // index 1-11
    const [loading, setLoading] = useState(false);
    const [actionId, setActionId] = useState(null); // Pour loader individuel
    const [showPalette, setShowPalette] = useState(false);
    const [showEditPalette, setShowEditPalette] = useState(false);
    const inputRef = useRef(null);
    const paletteRef = useRef(null);

    // Focus sur l'input à l'ouverture
    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    // Fermer la palette si on clique ailleurs
    useEffect(() => {
        function handleClickOutside(event) {
            if (paletteRef.current && !paletteRef.current.contains(event.target)) {
                setShowPalette(false);
                setShowEditPalette(false);
            }
        }
        if (showPalette || showEditPalette) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPalette, showEditPalette]);

    // Filtrer les catégories selon la saisie utilisateur
    const filteredCategories = categories.filter(cat =>
        cat && cat.name && cat.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    const handleEdit = (cat) => {
        setEditId(cat.id);
        setEditName(cat.name);
        setEditColor(cat.color || 1);
    };

    const handleEditSave = async (id) => {
        setLoading(true);
        setActionId(id);
        try {
            await apiService.updateElementById('/employee/category', id, { name: editName, color: editColor });
            setCategories(cats => cats.map(c => c.id === id ? { ...c, name: editName, color: editColor } : c));
            notify("Nom modifié", "", 2);
            setEditId(null);
        } catch {
            notify("Erreur lors de la modification", "", 3);
        }
        setLoading(false);
        setActionId(null);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer cette étiquette ?")) return;
        setLoading(true);
        setActionId(id);
        try {
            await apiService.updateElementById('/employee/delete-category', id, {});
            setCategories(cats => cats.filter(c => c.id !== id));
            notify("Étiquette supprimée", "", 2);
        } catch {
            notify("Erreur lors de la suppression", "", 3);
        }
        setLoading(false);
        setActionId(null);
    };

    const handleCreate = async () => {
        const name = inputValue.trim();
        if (!name) return;
        if (categories.some(cat => cat.name.toLowerCase() === name.toLowerCase())) {
            notify("Cette étiquette existe déjà.", "", 3);
            return;
        }
        setLoading(true);
        setActionId('create');
        try {
            const res = await apiService.saveElement('/employee/category', { name, color: inputColor });
            setCategories(cats => [...cats, res.data.result]);
            setInputValue('');
            setInputColor(1);
            if (inputRef.current) inputRef.current.focus();
            notify("Étiquette créée", "", 2);
        } catch {
            notify("Erreur lors de la création", "", 3);
        }
        setLoading(false);
        setActionId(null);
    };

    // Palette compacte en popover
    const ColorPopover = ({ selectedColor, setColor, onClose }) => (
        <div
            ref={paletteRef}
            className="absolute z-20 mt-2 p-2 bg-white border rounded shadow flex flex-wrap gap-1"
            style={{ minWidth: 120 }}
        >
            {TAG_COLORS.map((hex, idx) => (
                <button
                    key={idx}
                    type="button"
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-150
                        ${selectedColor === idx + 1 ? 'border-black scale-110 shadow' : 'border-gray-200 opacity-80 hover:opacity-100'}
                    `}
                    style={{ backgroundColor: hex }}
                    onClick={() => { setColor(idx + 1); onClose(); }}
                    aria-label={`Choisir la couleur ${idx + 1}`}
                    title={`Couleur ${idx + 1}`}
                >
                    {selectedColor === idx + 1 && <span className="w-2 h-2 bg-white rounded-full border" />}
                </button>
            ))}
        </div>
    );

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[--color-primary]">Gérer les étiquettes</h2>
                <button
                    className="btn btn-xs btn-outline text-[--color-orange] border-[--color-orange] hover:bg-[--color-orange]/10 rounded-full"
                    onClick={onClose}
                    aria-label="Fermer"
                >
                    Fermer
                </button>
            </div>
            <div className="mb-6">
                <div className="flex gap-2 items-center relative">
                    <div className="relative flex-1">
                        <input
                            ref={inputRef}
                            className="input input-sm input-bordered p-3 w-full rounded-md focus:border-[--color-primary] focus:ring-1 focus:ring-[--color-primary] transition"
                            type="text"
                            placeholder="Rechercher ou ajouter une étiquette"
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                            disabled={loading}
                            aria-label="Nom de l'étiquette"
                        />
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 flex items-center justify-center"
                            style={{ backgroundColor: TAG_COLORS[inputColor - 1], borderColor: '#ccc' }}
                            onClick={() => setShowPalette(v => !v)}
                            tabIndex={-1}
                            aria-label="Changer la couleur"
                            title="Changer la couleur"
                            disabled={loading}
                        >
                            {/* Cercle de couleur */}
                        </button>
                        {showPalette && (
                            <ColorPopover
                                selectedColor={inputColor}
                                setColor={c => setInputColor(c)}
                                onClose={() => setShowPalette(false)}
                            />
                        )}
                    </div>
                    <button
                        className="btn btn-sm bg-[--color-primary] text-white rounded-md px-4 flex items-center gap-1 hover:bg-[--bg-hover] transition disabled:opacity-60"
                        onClick={handleCreate}
                        disabled={loading || !inputValue.trim()}
                        title="Ajouter l'étiquette"
                        aria-label="Ajouter l'étiquette"
                    >
                        {loading && actionId === 'create' ? <FiLoader className="animate-spin" /> : <FiPlus />}
                        Ajouter
                    </button>
                </div>
            </div>
            <ul className="divide-y divide-gray-100">
                {categories.length === 0 && !inputValue && (
                    <li className="text-center text-gray-400 py-4">Aucune étiquette pour le moment.</li>
                )}
                {filteredCategories.length === 0 && inputValue ? (
                    <li className="text-center text-gray-400 py-4">Aucune étiquette trouvée.</li>
                ) : (
                    filteredCategories.map(cat => (
                        <li
                            key={cat.id}
                            className="flex items-center py-3 group hover:bg-gray-50 transition"
                        >
                            <span
                                className="w-5 h-5 rounded-full mr-2 border"
                                style={{ backgroundColor: TAG_COLORS[(cat.color || 1) - 1] }}
                                title={`Couleur ${cat.color}`}
                                aria-label={`Couleur ${cat.color}`}
                            ></span>
                            {editId === cat.id ? (
                                <>
                                    <input
                                        className="input input-sm input-bordered flex-1 rounded-md"
                                        value={editName}
                                        onChange={e => setEditName(e.target.value)}
                                        disabled={loading}
                                        autoFocus
                                        aria-label="Modifier le nom"
                                    />
                                    <div className="relative ml-2">
                                        <button
                                            type="button"
                                            className="w-6 h-6 rounded-full border-2 flex items-center justify-center"
                                            style={{ backgroundColor: TAG_COLORS[editColor - 1], borderColor: '#ccc' }}
                                            onClick={() => setShowEditPalette(v => !v)}
                                            tabIndex={-1}
                                            aria-label="Changer la couleur"
                                            title="Changer la couleur"
                                            disabled={loading}
                                        />
                                        {showEditPalette && (
                                            <ColorPopover
                                                selectedColor={editColor}
                                                setColor={c => setEditColor(c)}
                                                onClose={() => setShowEditPalette(false)}
                                            />
                                        )}
                                    </div>
                                    <button
                                        className="btn btn-xs btn-success ml-2 rounded-full flex items-center justify-center"
                                        onClick={() => handleEditSave(cat.id)}
                                        disabled={loading || !editName.trim()}
                                        title="Valider"
                                        aria-label="Valider"
                                    >
                                        {loading && actionId === cat.id ? <FiLoader className="animate-spin" /> : <FiCheck />}
                                    </button>
                                    <button
                                        className="btn btn-xs btn-outline ml-1 rounded-full flex items-center justify-center"
                                        onClick={() => setEditId(null)}
                                        disabled={loading}
                                        title="Annuler"
                                        aria-label="Annuler"
                                    >
                                        <FiX />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span className="flex-1 truncate text-gray-700">{cat.name}</span>
                                    <button
                                        className="btn btn-xs btn-outline ml-2 rounded-full flex items-center justify-center opacity-70 group-hover:opacity-100 transition"
                                        onClick={() => handleEdit(cat)}
                                        disabled={loading}
                                        title="Modifier"
                                        aria-label="Modifier"
                                    >
                                        <FiEdit2 />
                                    </button>
                                    <button
                                        className="btn btn-xs btn-error ml-1 rounded-full flex items-center justify-center opacity-70 group-hover:opacity-100 transition"
                                        onClick={() => handleDelete(cat.id)}
                                        disabled={loading}
                                        title="Supprimer"
                                        aria-label="Supprimer"
                                    >
                                        {loading && actionId === cat.id ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                                    </button>
                                </>
                            )}
                        </li>
                    ))
                )}
            </ul>
        </div>
    );
}

export default CategoryManager;